import React from "react";
import { Box, Chip, Divider, Grid, Typography } from "@mui/material";
import { ModalCore } from "src/Pages/components";
import { TitleWithValueCore } from "src/Pages/components/TitleWithValueCore/TitleWithValueCore";
import { IListBordero } from "src/Contexts/Lancamentos/Contrato/ContratosTypes";
import { FormatDateBR } from "src/shared/Utils/FormatDateBR";
import { FormatMoney } from "src/shared/Utils/FormatMoney";
import { cpfOurCnpj } from "src/shared/Utils/MaskCpfCnpj";
import { GetDateUSSemValue } from "src/shared/Utils/FormatDateBR";
import { CheckIcon, CloseIcon } from "src/Pages/components/Icons/Icons";
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import { statusContract } from "../../../0 - Contratos/Components/FormCancelContrato/FormCancelContrato";
import { objStatusContractColor } from "../../../0 - Contratos/Components/table/TableContratos";

interface IModalVisualizarParcela {
  openModal: boolean;
  setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
  parcela: IListBordero["data"][0] | null;
}

export const ModalVisualizarParcela = ({
  openModal,
  setOpenModal,
  parcela,
}: IModalVisualizarParcela) => {
  if (!parcela) return null;

  const getStatusParcela = () => {
    if (parcela.is_parcela_recebida) {
      return (
        <Chip
          sx={{ backgroundColor: "#EBF9EF", color: "#069A35FF" }}
          label="Recebida"
          icon={<CheckIcon color="success" sx={{ fontSize: ".9rem" }} />}
        />
      );
    }
    const vencimento = parcela.data_vencimento_parcela;
    const hoje = GetDateUSSemValue();

    if (vencimento && vencimento < hoje) {
      return (
        <Chip
          sx={{ backgroundColor: "#FEF2F2", color: "#EF4444" }}
          label="Vencida"
          icon={<CloseIcon color="error" sx={{ fontSize: ".9rem" }} />}
        />
      );
    }
    return (
      <Chip
        sx={{ backgroundColor: "#EDEDEDC0", color: "#6d6d6d" }}
        label="Pendente"
        icon={<CloseIcon sx={{ fontSize: ".9rem", color: "#6d6d6d" }} />}
      />
    );
  };

  const isPF = parcela.tipo_contrato === "PF";

  return (
    <ModalCore
      open={openModal}
      setOpen={setOpenModal}
      sxStyle={{ width: "900px", maxWidth: "95vw" }}
      title={`Informações da Parcela #${Number(parcela.numero_parcela) === 0 ? "Adesão" : parcela.numero_parcela}`}
      subTitle={`Proposta: ${parcela.cod_proposta?.trim() || ""}`}
    >
      <Box sx={{ padding: "8px" }}>
        {/* Status e Tipo de Contrato */}
        <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap", alignItems: "center" }}>
          <Chip
            label={statusContract[parcela.status_contratos]}
            sx={{
              backgroundColor: objStatusContractColor[parcela.status_contratos],
              color: "white",
              fontWeight: "500",
            }}
          />

          <Chip
            label={parcela.tipo_contrato}
            sx={{
              backgroundColor: isPF ? "#e4a022" : "#9357f5",
              color: "white",
              fontWeight: "bold",
            }}
          />

          {getStatusParcela()}

        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Informações do Cliente */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold", color: "#3b3b3b" }}>
          Dados do Cliente
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <TitleWithValueCore
              title={isPF ? "Titular:" : "Razão Social:"}
              value={isPF ? parcela.titular_pf : parcela.razao_social_pj}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TitleWithValueCore
              title={isPF ? "CPF:" : "CNPJ:"}
              value={cpfOurCnpj({
                text: String(parcela.cpf_cnpj),
                type: isPF ? "cpf" : "cnpj",
              })}
            />
          </Grid>
        </Grid>

        <Divider sx={{ mb: 3 }} />

        {/* Informações do Contrato */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold", color: "#3b3b3b" }}>
          Informações do Contrato
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <TitleWithValueCore
              title="Operadora:"
              value={`${parcela.code_operadoras} - ${parcela.name_operadoras}`}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TitleWithValueCore
              title="Modalidade:"
              value={`${parcela.code_modalidades} - ${parcela.name_modalidades}`}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TitleWithValueCore
              title="Vidas:"
              value={String(parcela.contratos_vidas)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TitleWithValueCore
              title="Tipo de Contrato:"
              value={parcela.nome_tipo_contrato || parcela.tipo_contrato}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TitleWithValueCore
              title="Valor do Contrato:"
              value={`R$ ${FormatMoney(parcela.valor_contrato)}`}
              sxStyleValue={{ color: "#3cb916", fontWeight: "600" }}
            />
          </Grid>
        </Grid>

        <Divider sx={{ mb: 3 }} />

        {/* Datas Importantes */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold", color: "#3b3b3b" }}>
          Datas
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <TitleWithValueCore
              title="Data de Vencimento:"
              value={parcela.data_vencimento_parcela ? FormatDateBR(parcela.data_vencimento_parcela) : "-"}
              sxStyleValue={{ color: "#0e6aff", fontWeight: "600" }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TitleWithValueCore
              title="Data de Recebimento:"
              value={parcela.data_recebimento ? FormatDateBR(parcela.data_recebimento) : "-"}
              sxStyleValue={{ color: parcela.data_recebimento ? "#0e6aff" : undefined, fontWeight: parcela.data_recebimento ? "600" : undefined }}
            />
          </Grid>
          {/* <Grid item xs={12} md={4}>
            <TitleWithValueCore
              title="Data de Previsão:"
              value={parcela.data_previsao ? FormatDateBR(parcela.data_previsao) : "-"}
              sxStyleValue={{ color: parcela.data_previsao ? "#0e6aff" : undefined, fontWeight: parcela.data_previsao ? "600" : undefined }}
            />
          </Grid> */}
        </Grid>

        <Divider sx={{ mb: 3 }} />

        {/* Valores Financeiros */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold", color: "#3b3b3b" }}>
          Valores Financeiros
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <TitleWithValueCore
              title="Valor Recebido:"
              value={parcela.valor_recebido ? `R$ ${FormatMoney(parcela.valor_recebido)}` : "-"}
              sxStyleValue={{ color: parcela.valor_recebido ? "#3cb916" : undefined, fontWeight: parcela.valor_recebido ? "600" : undefined }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TitleWithValueCore
              title="Valor Divergente:"
              value={`R$ ${FormatMoney(parcela.valor_divergente_contrato_to_recebido || "0.00")}`}
              sxStyleValue={{
                color: Number(parcela.valor_divergente_contrato_to_recebido) >= 0 ? "#3cb916" : "#FF4E48FF",
                fontWeight: "600"
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TitleWithValueCore
              title={parcela.is_parcela_recebida ? "Lucro da Parcela:" : "Lucro previsto:"}
              value={`R$ ${FormatMoney(String(parcela.valor_lucro_parcela || "0.00"))}`}
              sxStyleValue={{ color: "#3cb916", fontWeight: "600" }}
            />
          </Grid>
        </Grid>

        <Divider sx={{ mb: 3 }} />

        {/* Comissões - Valores */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold", color: "#3b3b3b" }}>
          Comissões - Valores
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TitleWithValueCore
                title="Corretora:"
                value={`R$ ${FormatMoney(parcela.valor_comissao_corretora)}`}
                sxStyleValue={{ color: "#3cb916", fontWeight: "600" }}
              />
              {parcela.is_vitalicio_corretora && <AllInclusiveIcon fontSize="small" color="primary" />}
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TitleWithValueCore
                title="Corretor:"
                value={`R$ ${FormatMoney(parcela.valor_comissao_corretor)}`}
                sxStyleValue={{ color: "#3cb916", fontWeight: "600" }}
              />
              {parcela.is_vitalicio_corretor && <AllInclusiveIcon fontSize="small" color="primary" />}
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TitleWithValueCore
                title="Supervisor:"
                value={`R$ ${FormatMoney(parcela.valor_comissao_supervisor || "0.00")}`}
                sxStyleValue={{ color: "#3cb916", fontWeight: "600" }}
              />
              {parcela.is_vitalicio_supervisor && <AllInclusiveIcon fontSize="small" color="primary" />}
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TitleWithValueCore
                title="Gerente:"
                value={`R$ ${FormatMoney(parcela.valor_comissao_gerente || "0.00")}`}
                sxStyleValue={{ color: "#3cb916", fontWeight: "600" }}
              />
              {parcela.is_vitalicio_gerente && <AllInclusiveIcon fontSize="small" color="primary" />}
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ mb: 3 }} />

        {/* Comissões - Porcentagens */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold", color: "#3b3b3b" }}>
          Comissões - Porcentagens
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TitleWithValueCore
                title="Corretora:"
                value={`${parcela.porcentagem_comissao_corretora?.replace(".", ",") || "0,00"}%`}
                sxStyleValue={{ color: "#3980f5", fontWeight: "600" }}
              />
              {parcela.is_vitalicio_corretora && <AllInclusiveIcon fontSize="small" color="primary" />}
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TitleWithValueCore
                title="Corretor:"
                value={`${parcela.porcentagem_comissao_corretor?.replace(".", ",") || "0,00"}%`}
                sxStyleValue={{ color: "#3980f5", fontWeight: "600" }}
              />
              {parcela.is_vitalicio_corretor && <AllInclusiveIcon fontSize="small" color="primary" />}
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TitleWithValueCore
                title="Supervisor:"
                value={`${parcela.porcentagem_comissao_supervisor?.replace(".", ",") || "0,00"}%`}
                sxStyleValue={{ color: "#3980f5", fontWeight: "600" }}
              />
              {parcela.is_vitalicio_supervisor && <AllInclusiveIcon fontSize="small" color="primary" />}
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TitleWithValueCore
                title="Gerente:"
                value={`${parcela.porcentagem_comissao_gerente?.replace(".", ",") || "0,00"}%`}
                sxStyleValue={{ color: "#3980f5", fontWeight: "600" }}
              />
              {parcela.is_vitalicio_gerente && <AllInclusiveIcon fontSize="small" color="primary" />}
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ mb: 3 }} />

        {/* Produtores */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold", color: "#3b3b3b" }}>
          Produtores
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <TitleWithValueCore
              title="Corretor:"
              value={parcela.nome_produtor ? `${parcela.id_corretor} - ${parcela.nome_produtor}` : "-"}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TitleWithValueCore
              title="Supervisor:"
              value={parcela.nome_supervisor ? `${parcela.id_supervisor} - ${parcela.nome_supervisor}` : "-"}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TitleWithValueCore
              title="Gerente:"
              value={parcela.nome_gerente ? `${parcela.id_gerente} - ${parcela.nome_gerente}` : "-"}
            />
          </Grid>
        </Grid>

        <Divider sx={{ mb: 3 }} />

        {/* Status de Recibos */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold", color: "#3b3b3b" }}>
          Status de Pagamentos
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Typography sx={{ fontSize: "0.9rem", }}>Corretor:</Typography>
              {parcela.valor_comissao_corretor !== "0.00" ? (
                <>
                  {parcela.is_recibo_corretor && (
                    <Chip
                      sx={{ backgroundColor: "#EBF9EF", color: "#069A35FF" }}
                      label="Pago"
                      icon={<CheckIcon color="success" sx={{ fontSize: ".9rem" }} />}
                      size="small"
                    />
                  )}

                  {!parcela.is_recibo_corretor && parcela.is_parcela_recebida && (
                    <Chip
                      sx={{ backgroundColor: "#FEF2F2", color: "#EF4444" }}
                      label="Pendente"
                      icon={<CloseIcon color="error" sx={{ fontSize: ".9rem" }} />}
                      size="small"
                    />
                  )}


                  {parcela.data_recibo_corretor && (
                    <Typography sx={{ fontSize: "0.85rem", color: "#0e6aff" }}>
                      {FormatDateBR(parcela.data_recibo_corretor)}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography sx={{ fontSize: "0.9rem", color: "#999" }}>-</Typography>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Typography sx={{ fontSize: "0.9rem", }}>Supervisor:</Typography>
              {parcela.valor_comissao_supervisor !== "0.00" ? (
                <>
                  {parcela.is_recibo_supervisor && (
                    <Chip
                      sx={{ backgroundColor: "#EBF9EF", color: "#069A35FF" }}
                      label="Pago"
                      icon={<CheckIcon color="success" sx={{ fontSize: ".9rem" }} />}
                      size="small"
                    />
                  )}

                  {!parcela.is_recibo_supervisor && parcela.is_parcela_recebida && (
                    <Chip
                      sx={{ backgroundColor: "#FEF2F2", color: "#EF4444" }}
                      label="Pendente"
                      icon={<CloseIcon color="error" sx={{ fontSize: ".9rem" }} />}
                      size="small"
                    />
                  )}
                  {parcela.data_recibo_supervisor && (
                    <Typography sx={{ fontSize: "0.85rem", color: "#0e6aff" }}>
                      {FormatDateBR(parcela.data_recibo_supervisor)}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography sx={{ fontSize: "0.9rem", color: "#999" }}>-</Typography>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Typography sx={{ fontSize: "0.9rem", }}>Gerente:</Typography>
              {parcela.valor_comissao_gerente !== "0.00" ? (
                <>
                  {parcela.is_recibo_gerente && (
                    <Chip
                      sx={{ backgroundColor: "#EBF9EF", color: "#069A35FF" }}
                      label="Pago"
                      icon={<CheckIcon color="success" sx={{ fontSize: ".9rem" }} />}
                      size="small"
                    />
                  )}

                  {!parcela.is_recibo_gerente && parcela.is_parcela_recebida && (
                    <Chip
                      sx={{ backgroundColor: "#FEF2F2", color: "#EF4444" }}
                      label="Pendente"
                      icon={<CloseIcon color="error" sx={{ fontSize: ".9rem" }} />}
                      size="small"
                    />
                  )}
                  {parcela.data_recibo_gerente && (
                    <Typography sx={{ fontSize: "0.85rem", color: "#0e6aff" }}>
                      {FormatDateBR(parcela.data_recibo_gerente)}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography sx={{ fontSize: "0.9rem", color: "#999" }}>-</Typography>
              )}
            </Box>
          </Grid>
        </Grid>

        {/* Observações */}
        {parcela.observacao && (
          <>
            <Divider sx={{ mb: 3 }} />
            <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold", color: "#3b3b3b" }}>
              Observações
            </Typography>
            <Box
              sx={{
                padding: 2,
                backgroundColor: "#f5f5f5",
                borderRadius: 1,
                mb: 2,
              }}
            >
              <Typography sx={{ fontSize: "0.9rem", color: "#3b3b3b" }}>
                {parcela.observacao}
              </Typography>
            </Box>
          </>
        )}

        {/* Informações Adicionais */}
        <Divider sx={{ mb: 3 }} />
        <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold", color: "#3b3b3b" }}>
          Informações Adicionais
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TitleWithValueCore
              title="ID Serial:"
              value={parcela.id_serial || "-"}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TitleWithValueCore
              title="ID Contrato:"
              value={parcela.id_contrato || "-"}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TitleWithValueCore
              title="Criado em:"
              value={parcela.created_at ? FormatDateBR(parcela.created_at) : "-"}
              sxStyleValue={{ color: "#0e6aff" }}
            />
          </Grid>
        </Grid>
      </Box>
    </ModalCore>
  );
};

